import React, { useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';

import { PROCESSING_ACTIVITY_LEGAL_BASIS_OPTIONS, PROCESSING_ACTIVITY_RETENTION_UNIT_OPTIONS } from '../processingActivityEditor.constants';
import { isLegitimateInterestsBasis } from '../processingActivityEditor.parsers';
import type { ProcessingActivityEditorPurpose } from '../processingActivityEditor.types';
import {
  buildProcessingActivityEditorValidationMessages,
  type PurposeDialogValidationErrors,
  validatePurposeForDialog,
} from '../processingActivityEditor.validation';

type ProcessingActivityPurposeDialogProps = {
  initialPurpose?: ProcessingActivityEditorPurpose | null;
  onClose: () => void;
  onSave: (purpose: ProcessingActivityEditorPurpose) => void;
  open: boolean;
};

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  liaNotice: {
    backgroundColor: theme.palette.warning.light,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
  },
}));

const createEmptyPurpose = (): ProcessingActivityEditorPurpose => ({
  clientId: `purpose-${Math.random().toString(36).slice(2, 11)}`,
  purposeText: '',
  legalBasis: '',
  legalBasisComment: '',
  retentionTrigger: '',
  retentionValue: null,
  retentionUnit: '',
  retentionComment: '',
  sortOrder: 0,
  isSoftDeleted: false,
});

const ProcessingActivityPurposeDialog: React.FC<ProcessingActivityPurposeDialogProps> = ({ initialPurpose, onClose, onSave, open }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [purpose, setPurpose] = useState<ProcessingActivityEditorPurpose>(createEmptyPurpose());
  const [fieldErrors, setFieldErrors] = useState<PurposeDialogValidationErrors>({});

  const validationMessages = useMemo(() => buildProcessingActivityEditorValidationMessages(t), [t]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextPurpose = initialPurpose ? { ...initialPurpose } : createEmptyPurpose();
    setPurpose(nextPurpose);
    setFieldErrors(initialPurpose ? validatePurposeForDialog(nextPurpose, validationMessages) : {});
  }, [initialPurpose, open, validationMessages]);

  const showLiaNotice = isLegitimateInterestsBasis(purpose.legalBasis);

  const clearFieldError = (field: keyof PurposeDialogValidationErrors) => {
    setFieldErrors(current => (current[field] ? { ...current, [field]: undefined } : current));
  };

  const handleSave = () => {
    const errors = validatePurposeForDialog(purpose, validationMessages);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    onSave(purpose);
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <DialogTitle>
        {t(
          initialPurpose
            ? processing_activities_messages.editor.purpose_dialog.edit_title
            : processing_activities_messages.editor.purpose_dialog.create_title,
        )}
      </DialogTitle>
      <DialogContent>
        <Typography className={styles.field} color='textSecondary' variant='body2'>
          {t(processing_activities_messages.editor.purpose_dialog.subtitle)}
        </Typography>
        <TextField
          className={styles.field}
          error={Boolean(fieldErrors.purposeText)}
          fullWidth
          helperText={fieldErrors.purposeText}
          label={t(processing_activities_messages.editor.fields.purpose)}
          multiline
          onChange={event => {
            setPurpose(current => ({ ...current, purposeText: event.target.value }));
            clearFieldError('purposeText');
          }}
          required
          rows={3}
          value={purpose.purposeText}
          variant='outlined'
        />
        <FormControl className={styles.field} error={Boolean(fieldErrors.legalBasis)} fullWidth required variant='outlined'>
          <InputLabel>{t(processing_activities_messages.editor.fields.legal_basis)}</InputLabel>
          <Select
            label={t(processing_activities_messages.editor.fields.legal_basis)}
            onChange={event => {
              const nextLegalBasis = event.target.value as ProcessingActivityEditorPurpose['legalBasis'];
              setPurpose(current => ({
                ...current,
                legalBasis: nextLegalBasis,
              }));
              clearFieldError('legalBasis');
              if (!isLegitimateInterestsBasis(nextLegalBasis)) {
                clearFieldError('legalBasisComment');
              }
            }}
            value={purpose.legalBasis}
          >
            <MenuItem value=''>{t(processing_activities_messages.editor.fields.legal_basis_placeholder)}</MenuItem>
            {PROCESSING_ACTIVITY_LEGAL_BASIS_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {t(processing_activities_messages.editor.legal_basis_with_article[option])}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.legalBasis && <FormHelperText>{fieldErrors.legalBasis}</FormHelperText>}
        </FormControl>
        <TextField
          className={styles.field}
          error={Boolean(fieldErrors.legalBasisComment)}
          fullWidth
          helperText={fieldErrors.legalBasisComment}
          label={t(processing_activities_messages.editor.fields.legal_basis_comment)}
          multiline
          onChange={event => {
            setPurpose(current => ({ ...current, legalBasisComment: event.target.value }));
            clearFieldError('legalBasisComment');
          }}
          placeholder={t(processing_activities_messages.editor.fields.legal_basis_comment_placeholder)}
          required={showLiaNotice}
          rows={3}
          value={purpose.legalBasisComment}
          variant='outlined'
        />
        {showLiaNotice && (
          <div className={styles.liaNotice}>
            <Typography variant='subtitle2'>{t(processing_activities_messages.editor.lia_required_title)}</Typography>
            <Typography variant='body2'>{t(processing_activities_messages.editor.lia_required_body)}</Typography>
          </div>
        )}
        <TextField
          className={styles.field}
          error={Boolean(fieldErrors.retentionTrigger)}
          fullWidth
          helperText={fieldErrors.retentionTrigger}
          label={t(processing_activities_messages.editor.fields.retention_trigger)}
          onChange={event => {
            setPurpose(current => ({ ...current, retentionTrigger: event.target.value }));
            clearFieldError('retentionTrigger');
          }}
          required
          value={purpose.retentionTrigger}
          variant='outlined'
        />
        <TextField
          className={styles.field}
          error={Boolean(fieldErrors.retentionValue)}
          fullWidth
          helperText={fieldErrors.retentionValue}
          inputProps={{ min: 0, step: 1 }}
          label={t(processing_activities_messages.editor.fields.retention_value)}
          onChange={event => {
            const nextValue = event.target.value.trim();
            setPurpose(current => ({
              ...current,
              retentionValue: nextValue ? Number(nextValue) : null,
            }));
            clearFieldError('retentionValue');
          }}
          required
          type='number'
          value={purpose.retentionValue ?? ''}
          variant='outlined'
        />
        <FormControl className={styles.field} error={Boolean(fieldErrors.retentionUnit)} fullWidth required variant='outlined'>
          <InputLabel>{t(processing_activities_messages.editor.fields.retention_unit)}</InputLabel>
          <Select
            label={t(processing_activities_messages.editor.fields.retention_unit)}
            onChange={event => {
              setPurpose(current => ({
                ...current,
                retentionUnit: event.target.value as ProcessingActivityEditorPurpose['retentionUnit'],
              }));
              clearFieldError('retentionUnit');
            }}
            value={purpose.retentionUnit}
          >
            <MenuItem value=''>{t(processing_activities_messages.editor.fields.legal_basis_placeholder)}</MenuItem>
            {PROCESSING_ACTIVITY_RETENTION_UNIT_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {t(processing_activities_messages.editor.retention_units[option])}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.retentionUnit && <FormHelperText>{fieldErrors.retentionUnit}</FormHelperText>}
        </FormControl>
        <TextField
          className={styles.field}
          error={Boolean(fieldErrors.retentionComment)}
          fullWidth
          helperText={fieldErrors.retentionComment}
          label={t(processing_activities_messages.editor.fields.retention_comment)}
          multiline
          onChange={event => {
            setPurpose(current => ({ ...current, retentionComment: event.target.value }));
            clearFieldError('retentionComment');
          }}
          required
          rows={3}
          value={purpose.retentionComment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={false}
          icon={false}
          onClick={onClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handleSave}
          showLoader={false}
          textLike={false}
          variant='contained'
        >
          {t(general_messages.save)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessingActivityPurposeDialog;
